import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { organizationPoliciesService } from "@/services/organizationPoliciesService";
import type { OrganizationPolicy } from "@/types/workflowEngine";

export function OrganizationPoliciesPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: policy } = useQuery({
    queryKey: ["organization-policy"],
    queryFn: () => organizationPoliciesService.getEffectivePolicy(),
  });

  const [form, setForm] = useState({
    maxCreditsPerRun: 50,
    minConfidence: 0.5,
    sideEffectRequiresReview: true,
    redactInLogs: false,
    runEventDays: 90,
  });

  useEffect(() => {
    if (!policy) return;
    setForm({
      maxCreditsPerRun: Number(policy.costLimits?.maxCreditsPerRun ?? 50),
      minConfidence: Number(policy.approvalThreshold?.minConfidence ?? 0.5),
      sideEffectRequiresReview: Boolean(policy.approvalThreshold?.sideEffectRequiresReview ?? true),
      redactInLogs: Boolean(policy.piiRules?.redactInLogs ?? false),
      runEventDays: Number(policy.retention?.runEventDays ?? 90),
    });
  }, [policy]);

  const saveMutation = useMutation({
    mutationFn: () =>
      organizationPoliciesService.upsert({
        scope: "workspace",
        costLimits: { maxCreditsPerRun: form.maxCreditsPerRun },
        approvalThreshold: {
          minConfidence: form.minConfidence,
          sideEffectRequiresReview: form.sideEffectRequiresReview,
        },
        piiRules: { redactInLogs: form.redactInLogs },
        retention: { runEventDays: form.runEventDays },
      } as Partial<OrganizationPolicy>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-policy"] });
      toast({ title: "Workflow policies saved" });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to save", description: e.message, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow & AI Policies</CardTitle>
        <CardDescription>
          Organization defaults for workflow runs — cost limits, approvals, retention, and PII handling.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="max-credits">Max credits per run</Label>
            <Input
              id="max-credits"
              type="number"
              min={1}
              value={form.maxCreditsPerRun}
              onChange={(e) => setForm((f) => ({ ...f, maxCreditsPerRun: Number(e.target.value) }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="min-confidence">Minimum confidence for auto-actions</Label>
            <Input
              id="min-confidence"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.minConfidence}
              onChange={(e) => setForm((f) => ({ ...f, minConfidence: Number(e.target.value) }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="retention-days">Run event retention (days)</Label>
            <Input
              id="retention-days"
              type="number"
              min={7}
              value={form.runEventDays}
              onChange={(e) => setForm((f) => ({ ...f, runEventDays: Number(e.target.value) }))}
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <p className="text-sm font-medium">Require review for side effects</p>
            <p className="text-xs text-muted-foreground">ClickUp, Gmail, and HTTP tools pause for approval when enabled.</p>
          </div>
          <Switch
            checked={form.sideEffectRequiresReview}
            onCheckedChange={(v) => setForm((f) => ({ ...f, sideEffectRequiresReview: v }))}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <p className="text-sm font-medium">Redact PII in run logs</p>
            <p className="text-xs text-muted-foreground">Mask emails and phone numbers in workflow event snapshots.</p>
          </div>
          <Switch
            checked={form.redactInLogs}
            onCheckedChange={(v) => setForm((f) => ({ ...f, redactInLogs: v }))}
          />
        </div>

        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving…" : "Save policies"}
        </Button>
      </CardContent>
    </Card>
  );
}
